/**
 * The pause screen. It covers the whole game — well, HUD preview and answer
 * choices — so pausing can't be used to think (docs/GAME_DESIGN.md § 1).
 * Resume has focus; Escape resumes. Quitting asks once, kindly.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '../components/Button.tsx';
import { useT } from '../i18n/index.ts';
import { GameIcon } from './icons.tsx';

export interface PauseOverlayProps {
  onResume(): void;
  onQuit(): void;
  onOpenSettings?: () => void;
  compact: boolean;
}

export function PauseOverlay({ onResume, onQuit, onOpenSettings, compact }: PauseOverlayProps) {
  const t = useT();
  const titleId = useId();
  const [confirming, setConfirming] = useState(false);
  const resumeRef = useRef<HTMLButtonElement>(null);
  const keepRef = useRef<HTMLButtonElement>(null);

  // Put focus on the safe choice whenever the card changes.
  useEffect(() => {
    (confirming ? keepRef : resumeRef).current?.focus({ preventScroll: true });
  }, [confirming]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      // A dialog opened over the pause screen (in-game settings) handles its own Escape.
      if (e.target instanceof Element && e.target.closest('dialog')) return;
      e.preventDefault();
      if (confirming) setConfirming(false);
      else onResume();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirming, onResume]);

  return (
    <div className={`pause${compact ? ' pause--compact' : ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <div className="pause__card">
        {confirming ? (
          <>
            <h2 id={titleId} className="pause__title">
              {t('game.quitTitle')}
            </h2>
            {!compact && <p className="pause__text">{t('game.quitText')}</p>}
            <div className="pause__actions">
              <Button ref={keepRef} variant="primary" size={compact ? 'md' : 'lg'} icon="play" block onClick={() => setConfirming(false)}>
                {t('game.resume')}
              </Button>
              <Button variant="secondary" icon="close" block onClick={onQuit}>
                {t('game.quitYes')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <span className="pause__badge" aria-hidden="true">
              <GameIcon name="pause" size="1.6em" />
            </span>
            <h2 id={titleId} className="pause__title">
              {t('game.pausedTitle')}
            </h2>
            {!compact && <p className="pause__text">{t('game.pausedText')}</p>}
            <div className="pause__actions">
              <Button ref={resumeRef} variant="primary" size={compact ? 'md' : 'lg'} icon="play" block onClick={onResume}>
                {t('game.resume')}
              </Button>
              {onOpenSettings && !compact && (
                <Button variant="secondary" icon="settings" block onClick={onOpenSettings}>
                  {t('game.settings')}
                </Button>
              )}
              <Button variant="ghost" icon="close" block onClick={() => setConfirming(true)}>
                {t('game.quit')}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
